// ==========================================================================
// 🚀 NEUROGUIDE FFT ENGINE — Universal QEEG Versiyası
// Cooley-Tukey FFT + Welch PSD + Peak Frequency
//
// DƏYİŞİKLİKLƏR:
//   1. Z-Score hesablamaları arxa planda qorunur.
//   2. Relative Power xəritələməsi üçün xam faizlər (0-100%) əlavə edildi.
//   3. Coherence xəritələməsi üçün real Pearson korrelyasiyası (0.0-1.0) əlavə edildi.
// ==========================================================================

const nextPowerOf2 = (n) => {
    let p = 1;
    while (p < n) p <<= 1;
    return p;
};

export const computeFFT = (signal) => {
    const N = nextPowerOf2(signal.length);
    const re = new Float64Array(N);
    const im = new Float64Array(N);
    for (let i = 0; i < signal.length; i++) re[i] = signal[i];

    let j = 0;
    for (let i = 1; i < N; i++) {
        let bit = N >> 1;
        for (; j & bit; bit >>= 1) j ^= bit;
        j ^= bit;
        if (i < j) {
            [re[i], re[j]] = [re[j], re[i]];
            [im[i], im[j]] = [im[j], im[i]];
        }
    }
    for (let len = 2; len <= N; len <<= 1) {
        const halfLen = len >> 1;
        const ang = -2.0 * Math.PI / len;
        const wRe = Math.cos(ang), wIm = Math.sin(ang);
        for (let i = 0; i < N; i += len) {
            let curRe = 1.0, curIm = 0.0;
            for (let k = 0; k < halfLen; k++) {
                const uRe = re[i+k], uIm = im[i+k];
                const vRe = re[i+k+halfLen]*curRe - im[i+k+halfLen]*curIm;
                const vIm = re[i+k+halfLen]*curIm + im[i+k+halfLen]*curRe;
                re[i+k] = uRe+vRe; im[i+k] = uIm+vIm;
                re[i+k+halfLen] = uRe-vRe; im[i+k+halfLen] = uIm-vIm;
                const nr = curRe*wRe - curIm*wIm;
                curIm = curRe*wIm + curIm*wRe;
                curRe = nr;
            }
        }
    }
    return { re, im };
};

const applyHannWindow = (signal) => {
    const N = signal.length;
    return signal.map((v, i) => v * (0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1))));
};

const computePowerSpectrum = (signal, sampleRate) => {
    const windowed = applyHannWindow(signal);
    const { re, im } = computeFFT(windowed);
    const N = re.length;
    const half = Math.floor(N / 2) + 1;
    const hannPower = 0.375 * N;

    const freqs  = new Float64Array(half);
    const powers = new Float64Array(half);
    for (let k = 0; k < half; k++) {
        freqs[k] = (k * sampleRate) / N;
        const raw = (re[k]*re[k] + im[k]*im[k]) / (sampleRate * hannPower);
        powers[k] = (k === 0 || k === half - 1) ? raw : raw * 2.0;
    }
    return { freqs, powers };
};

const bandMeanPSD = (freqs, powers, fLow, fHigh) => {
    let sum = 0, count = 0;
    for (let k = 0; k < freqs.length; k++) {
        if (freqs[k] >= fLow && freqs[k] < fHigh) { sum += powers[k]; count++; }
    }
    return count > 0 ? sum / count : 1e-12;
};

const peakFrequency = (freqs, powers, fLow, fHigh) => {
    let maxPow = -Infinity, maxIdx = -1;
    for (let k = 0; k < freqs.length; k++) {
        if (freqs[k] >= fLow && freqs[k] < fHigh && powers[k] > maxPow) {
            maxPow = powers[k]; maxIdx = k;
        }
    }
    if (maxIdx <= 0 || maxIdx >= freqs.length - 1) return freqs[maxIdx] || (fLow+fHigh)/2;
    const y0 = powers[maxIdx-1], y1 = powers[maxIdx], y2 = powers[maxIdx+1];
    const denom = 2*y1 - y0 - y2;
    if (denom === 0) return freqs[maxIdx];
    const offset = 0.5 * (y0 - y2) / denom;
    const res = freqs[1] - freqs[0];
    return Math.max(fLow, Math.min(fHigh - 0.01, freqs[maxIdx] + offset * res));
};

const BAND_RANGES = {
    delta:    [0.5,  4.0],
    theta:    [4.0,  8.0],
    alpha:    [8.0, 12.0],
    beta:     [12.0, 25.0],
    highbeta: [25.0, 40.0],
    beta1:    [12.0, 15.0],
    beta2:    [15.0, 18.0],
    beta3:    [18.0, 25.0],
};

const BAND_BW = {
    delta: 3.5, theta: 4.0, alpha: 4.0, beta: 13.0, highbeta: 15.0
};

const POWER_BANDS    = ['delta','theta','alpha','beta','highbeta'];
const PEAK_FREQ_BANDS = ['delta','theta','alpha','beta','highbeta','beta1','beta2','beta3'];

const ABS_POWER_NORM = {
    delta:    { mean: 3.5, sd: 0.6 },
    theta:    { mean: 3.0, sd: 0.5 },
    alpha:    { mean: 3.2, sd: 0.5 },
    beta:     { mean: 2.5, sd: 0.4 },
    highbeta: { mean: 2.0, sd: 0.4 },
};

const REL_POWER_NORM = {
    delta:    { mean: 1.30, sd: 0.25 },
    theta:    { mean: 1.10, sd: 0.25 },
    alpha:    { mean: 1.30, sd: 0.25 },
    beta:     { mean: 1.15, sd: 0.25 },
    highbeta: { mean: 0.80, sd: 0.25 },
};

const PEAK_FREQ_NORM = {
    delta:    { mean: 2.264, sd: 0.5 },
    theta:    { mean: 5.630, sd: 0.5 },
    alpha:    { mean: 9.594, sd: 0.5 },
    beta:     { mean: 17.593, sd: 1.0 },
    highbeta: { mean: 27.108, sd: 1.5 },
    beta1:    { mean: 13.142, sd: 0.5 },
    beta2:    { mean: 16.221, sd: 0.5 },
    beta3:    { mean: 20.917, sd: 1.0 },
};

const COH_NORM = {
    delta:    { mean: 0.45, sd: 0.15 },
    theta:    { mean: 0.45, sd: 0.15 },
    alpha:    { mean: 0.50, sd: 0.15 },
    beta:     { mean: 0.35, sd: 0.15 },
    highbeta: { mean: 0.30, sd: 0.15 },
};

const PHASE_NORM = {
    delta:    { mean: 0.0, sd: 0.15 },
    theta:    { mean: 0.0, sd: 0.15 },
    alpha:    { mean: 0.0, sd: 0.12 },
    beta:     { mean: 0.0, sd: 0.10 },
    highbeta: { mean: 0.0, sd: 0.10 },
};

export const processEdfFile = (buffer, callbacks, options = {}) => {
    const { setPatientName, setQeegResult } = callbacks;
    const hardwareGain = options.hardwareGain !== undefined ? options.hardwareGain : 4.0;
    const view = new DataView(buffer);
    const decoder = new TextDecoder('ascii');

    const readHeaderStr = (offset, length) =>
        decoder.decode(new Uint8Array(buffer, offset, length)).trim();

    const patientId  = readHeaderStr(8, 80)  || "Bilinmeyen Hasta";
    const numSignals = parseInt(readHeaderStr(252, 4), 10);
    const numRecords = parseInt(readHeaderStr(236, 8), 10);

    if (!numSignals || numSignals <= 0) throw new Error("EDF başlığı oxuna bilmədi.");

    let birthYear  = 2017;
    let recordYear = new Date().getFullYear();
    const yearMatches = patientId.match(/\d{4}/g);
    if (yearMatches) birthYear = parseInt(yearMatches[0], 10);
    const rawStartDate = readHeaderStr(168, 8);
    if (rawStartDate) {
        const parts = rawStartDate.split('.');
        if (parts.length === 3) {
            const sy = parseInt(parts[2], 10);
            recordYear = sy < 50 ? 2000 + sy : 1900 + sy;
        }
    }

    let dynamicName = "Bilinmeyen Hasta";
    const clean = patientId.replace(/_/g, ' ');
    const nameMatches = clean.match(/[a-zA-Z\s]{4,}/g);
    if (nameMatches) {
        dynamicName = nameMatches[nameMatches.length - 1]
            .trim().replace(/(^\w|\s\w)/g, m => m.toUpperCase());
    }
    if (typeof setPatientName === 'function') setPatientName(dynamicName);

    let channelLabels = [];
    let annotationsIdx = -1;
    for (let i = 0; i < numSignals; i++) {
        const label = readHeaderStr(256 + i*16, 16) || `CH_${i+1}`;
        const clean = label.trim().split('-')[0].replace('EEG','').trim();
        channelLabels.push(clean);
        if (label.toUpperCase().includes("ANNOTATIONS")) annotationsIdx = i;
    }

    let digMin = [], digMax = [], physMin = [], physMax = [], samplePoints = [];
    let off = 256 + numSignals*16 + numSignals*80;
    for (let i = 0; i < numSignals; i++) physMin.push(parseFloat(readHeaderStr(off + i*8, 8)) || 0);
    off += numSignals*8;
    for (let i = 0; i < numSignals; i++) physMax.push(parseFloat(readHeaderStr(off + i*8, 8)) || 100);
    off += numSignals*8;
    for (let i = 0; i < numSignals; i++) digMin.push(parseFloat(readHeaderStr(off + i*8, 8)) || -32768);
    off += numSignals*8;
    for (let i = 0; i < numSignals; i++) digMax.push(parseFloat(readHeaderStr(off + i*8, 8)) || 32767);
    off += numSignals*8 + numSignals*80;
    for (let i = 0; i < numSignals; i++) {
        samplePoints.push(parseInt(readHeaderStr(off + i*8, 8), 10) || 256);
    }

    let byteOffset   = 256 + numSignals * 256;
    let channelsData = Array.from({ length: numSignals }, () => []);

    for (let r = 0; r < numRecords; r++) {
        for (let ch = 0; ch < numSignals; ch++) {
            const n = samplePoints[ch];
            if (ch === annotationsIdx) { byteOffset += n*2; continue; }
            const scale = (digMax[ch]-digMin[ch]) === 0 ? 1
                : (physMax[ch]-physMin[ch]) / (digMax[ch]-digMin[ch]);
            for (let s = 0; s < n; s++) {
                if (byteOffset + 2 <= buffer.byteLength) {
                    channelsData[ch].push(
                        (view.getInt16(byteOffset, true) - digMin[ch]) * scale + physMin[ch]
                    );
                    byteOffset += 2;
                }
            }
        }
    }

    const t3Idx = channelLabels.indexOf('T3');
    const t4Idx = channelLabels.indexOf('T4');
    if (t3Idx !== -1 && t4Idx !== -1) {
        const a1 = channelsData[t3Idx], a2 = channelsData[t4Idx];
        for (let ch = 0; ch < numSignals; ch++) {
            if (ch === annotationsIdx || ch === t3Idx || ch === t4Idx) continue;
            for (let s = 0; s < channelsData[ch].length; s++) {
                channelsData[ch][s] -= (a1[s] + a2[s]) / 2;
            }
        }
    }

    const chList = channelLabels.filter((l, i) => i !== annotationsIdx && l !== '-' && l);

    let preFiltered = {};
    for (let ch = 0; ch < numSignals; ch++) {
        const label = channelLabels[ch];
        if (ch === annotationsIdx || label === '-' || !label) continue;
        const raw = channelsData[ch];
        if (!raw.length) continue;
        const sr = samplePoints[ch];
        const dt = 1.0 / sr;
        const alphaLP = dt / (1.0/(2*Math.PI*40) + dt);
        const rcHP    = 1.0 / (2*Math.PI*0.5);
        const alphaHP = rcHP / (rcHP + dt);
        let lastLP = raw[0], lastHP = 0, lastRaw = raw[0];
        const filtered = new Array(raw.length);
        for (let i = 0; i < raw.length; i++) {
            const lp = lastLP + alphaLP * (raw[i] - lastLP);
            const hp = alphaHP * (lastHP + lp - lastRaw);
            lastLP = lp; lastHP = hp; lastRaw = lp;
            filtered[i] = hp;
        }
        preFiltered[label] = { signal: filtered, sampleRate: sr };
    }

    const firstCh = chList.find(l => preFiltered[l]);
    if (!firstCh) {
        if (typeof setQeegResult === 'function') setQeegResult({});
        return;
    }

    const sr           = preFiltered[firstCh].sampleRate;
    const totalSamples = preFiltered[firstCh].signal.length;
    const epochSamples = Math.pow(2, Math.round(Math.log2(sr * 2)));
    const stepSamples  = Math.floor(epochSamples / 2);

    let cleanEpochs = [];
    for (let start = 0; start + epochSamples <= totalSamples; start += stepSamples) {
        let bad = false;
        for (const lbl of chList) {
            if (!preFiltered[lbl]) continue;
            const sl = preFiltered[lbl].signal.slice(start, start + epochSamples);
            const mean = sl.reduce((a,v) => a+v, 0) / sl.length;
            const variance = sl.reduce((a,v) => a+(v-mean)**2, 0) / sl.length;
            const range = Math.max(...sl) - Math.min(...sl);
            if (variance > 6000 || variance < 0.1 || range > 500) { bad = true; break; }
        }
        if (!bad) cleanEpochs.push(start);
    }
    const finalEpochs = cleanEpochs; // 30 saniyə limitini ləğv etdik, bütün təmiz dalğaları götürür

    const N_FFT = epochSamples;
    const half = Math.floor(N_FFT / 2) + 1;
    const hannPower = 0.375 * N_FFT;
    const HARDWARE_GAIN = hardwareGain;

    let accumPSD = {};
    chList.forEach(ch => accumPSD[ch] = new Float64Array(half));

    let accumCSDRe = {};
    let accumCSDIm = {};
    for (let i = 0; i < chList.length; i++) {
        for (let j = i + 1; j < chList.length; j++) {
            const key = `${chList[i]}_${chList[j]}`;
            accumCSDRe[key] = new Float64Array(half);
            accumCSDIm[key] = new Float64Array(half);
        }
    }

    let freqs = new Float64Array(half);
    for (let k = 0; k < half; k++) freqs[k] = (k * sr) / N_FFT;

    let epochCount = 0;

    finalEpochs.forEach(start => {
        let ffts = {};
        let validEpoch = true;
        
        chList.forEach(ch => {
            let seg = preFiltered[ch].signal.slice(start, start + N_FFT);
            if (seg.length < N_FFT) { validEpoch = false; return; }
            
            const hwGained = new Float64Array(N_FFT);
            for (let i = 0; i < N_FFT; i++) hwGained[i] = seg[i] * HARDWARE_GAIN;
            
            const windowed = applyHannWindow(Array.from(hwGained));
            const fft = computeFFT(windowed);
            ffts[ch] = fft;
            
            for (let k = 0; k < half; k++) {
                const re = fft.re[k];
                const im = fft.im[k];
                const raw = (re * re + im * im) / (sr * hannPower);
                accumPSD[ch][k] += (k === 0 || k === half - 1) ? raw : raw * 2.0;
            }
        });
        
        if (!validEpoch) return;
        epochCount++;
        
        for (let i = 0; i < chList.length; i++) {
            const chA = chList[i];
            const fftA = ffts[chA];
            for (let j = i + 1; j < chList.length; j++) {
                const chB = chList[j];
                const fftB = ffts[chB];
                const key = `${chA}_${chB}`;
                
                for (let k = 0; k < half; k++) {
                    const reA = fftA.re[k], imA = fftA.im[k];
                    const reB = fftB.re[k], imB = fftB.im[k];
                    
                    const csdRe = (reA * reB + imA * imB) / (sr * hannPower);
                    const csdIm = (imA * reB - reA * imB) / (sr * hannPower);
                    
                    const mult = (k === 0 || k === half - 1) ? 1.0 : 2.0;
                    accumCSDRe[key][k] += csdRe * mult;
                    accumCSDIm[key][k] += csdIm * mult;
                }
            }
        }
    });

    if (epochCount > 0) {
        chList.forEach(ch => {
            for (let k = 0; k < half; k++) accumPSD[ch][k] /= epochCount;
        });
        for (let i = 0; i < chList.length; i++) {
            for (let j = i + 1; j < chList.length; j++) {
                const key = `${chList[i]}_${chList[j]}`;
                for (let k = 0; k < half; k++) {
                    accumCSDRe[key][k] /= epochCount;
                    accumCSDIm[key][k] /= epochCount;
                }
            }
        }
    }

    let absPowerUv2  = {}; 
    let peakFreqHz   = {};
    let peakFreqZ    = {};
    let absPowerZ    = {};
    let relPowerZ    = {};
    let relPowerPct  = {}; 
    let ampAsymmetry = {};
    let coherence    = {}; 
    let rawCoherence = {}; 
    let phaseLag     = {};

    chList.forEach(label => {
        absPowerUv2[label] = {};
        absPowerZ[label]   = {};
        relPowerZ[label]   = {};
        relPowerPct[label] = {};

        POWER_BANDS.forEach(b => {
            const [fl, fh] = BAND_RANGES[b];
            let sumPsd = 0, countPsd = 0;
            for (let k = 0; k < half; k++) {
                if (freqs[k] >= fl && freqs[k] < fh) {
                    sumPsd += accumPSD[label][k];
                    countPsd++;
                }
            }
            const meanPsd = countPsd > 0 ? sumPsd / countPsd : 1e-12;
            absPowerUv2[label][b] = parseFloat(meanPsd.toFixed(4));

            const totalPow  = Math.max(meanPsd * BAND_BW[b], 1e-12);
            const lnAmp     = 0.5 * Math.log(totalPow);
            const norm      = ABS_POWER_NORM[b];
            absPowerZ[label][b] = parseFloat(((lnAmp - norm.mean) / norm.sd).toFixed(2));
        });

        const totalAllBands = POWER_BANDS.reduce((s, b) => s + absPowerUv2[label][b] * BAND_BW[b], 0);
        
        POWER_BANDS.forEach(b => {
            const bandTotalPow = absPowerUv2[label][b] * BAND_BW[b];
            const pct = totalAllBands > 0 ? (100 * bandTotalPow) / totalAllBands : 0;
            
            relPowerPct[label][b] = parseFloat(pct.toFixed(2));
            
            const log10pct = Math.log10(Math.max(pct, 0.01));
            const norm = REL_POWER_NORM[b];
            relPowerZ[label][b] = parseFloat(((log10pct - norm.mean) / norm.sd).toFixed(2));
        });

        peakFreqHz[label] = {};
        peakFreqZ[label]  = {};
        PEAK_FREQ_BANDS.forEach(b => {
            const [fl, fh] = BAND_RANGES[b];
            const hz = peakFrequency(freqs, accumPSD[label], fl, fh);
            peakFreqHz[label][b] = parseFloat(hz.toFixed(2));
            const norm = PEAK_FREQ_NORM[b];
            peakFreqZ[label][b]  = parseFloat(((hz - norm.mean) / norm.sd).toFixed(2));
        });
    });

    for (let i = 0; i < chList.length; i++) {
        for (let j = i + 1; j < chList.length; j++) {
            const chA = chList[i];
            const chB = chList[j];
            const key = `${chA}_${chB}`;
            const revKey = `${chB}_${chA}`;
            
            ampAsymmetry[key] = {};
            ampAsymmetry[revKey] = {};
            coherence[key]    = {};
            coherence[revKey] = {};
            rawCoherence[key] = {};
            rawCoherence[revKey] = {};
            phaseLag[key]     = {};
            phaseLag[revKey]  = {};

            POWER_BANDS.forEach(b => {
                const totalA  = Math.max(absPowerUv2[chA][b] * BAND_BW[b], 1e-12);
                const totalB  = Math.max(absPowerUv2[chB][b] * BAND_BW[b], 1e-12);
                const lnAmpA  = 0.5 * Math.log(totalA);
                const lnAmpB  = 0.5 * Math.log(totalB);
                const sdAsym  = ABS_POWER_NORM[b].sd * Math.sqrt(2);

                const asymZ = ((lnAmpA - lnAmpB) / sdAsym);
                ampAsymmetry[key][b]    = parseFloat(asymZ.toFixed(2));
                ampAsymmetry[revKey][b] = parseFloat((-asymZ).toFixed(2));

                const [fl, fh] = BAND_RANGES[b];
                let sumCoh = 0, countCoh = 0;
                let sumPhase = 0;

                for (let k = 0; k < half; k++) {
                    if (freqs[k] >= fl && freqs[k] < fh) {
                        const psdA = accumPSD[chA][k];
                        const psdB = accumPSD[chB][k];
                        if (psdA > 0 && psdB > 0) {
                            const cRe = accumCSDRe[key][k];
                            const cIm = accumCSDIm[key][k];
                            const magSq = cRe * cRe + cIm * cIm;
                            sumCoh += Math.sqrt(magSq / (psdA * psdB)); 
                            sumPhase += Math.atan2(cIm, cRe);
                        }
                        countCoh++;
                    }
                }
                
                const r = countCoh > 0 ? sumCoh / countCoh : 0.0;
                const rFixed = parseFloat(r.toFixed(3));
                rawCoherence[key][b]    = rFixed;
                rawCoherence[revKey][b] = rFixed;

                const rClamped = Math.min(r, 0.9999);
                const fisherZ  = 0.5 * Math.log((1 + rClamped) / (1 - rClamped));
                const normCoh  = COH_NORM[b] || { mean: 0.45, sd: 0.15 };
                const cohZ = ((fisherZ - normCoh.mean) / normCoh.sd);
                const cohZFixed = parseFloat(cohZ.toFixed(2));
                coherence[key][b]    = cohZFixed;
                coherence[revKey][b] = cohZFixed;

                const avgPhase = countCoh > 0 ? sumPhase / countCoh : 0.0;
                const normPhase = PHASE_NORM[b] || { mean: 0.0, sd: 0.15 };
                const phaseZ = (avgPhase - normPhase.mean) / normPhase.sd;
                phaseLag[key][b]    = parseFloat(phaseZ.toFixed(2));
                phaseLag[revKey][b] = parseFloat((-phaseZ).toFixed(2));
            });
        }
    }

    const result = {
        patient_info: {
            raw_header_id: patientId,
            detected_name: dynamicName,
            device:        "NeuroGuide-WelchPSD_19Ch",
            birth_year:    birthYear,
            record_year:   recordYear,
            age:           recordYear - birthYear,
            analyzed_seconds: epochCount // Analiz edilən təmizlənmiş saniyələrin sayı
        },
        raw_spectral_values: {
            absolute_power_psd_uv2hz: absPowerUv2,
        },
     qeeg_spectral_analysis: {
            absolute_power:      absPowerZ,    
            relative_power:      relPowerZ,    // DÜZƏLİŞ: relPowerPct YOX, relPowerZ olmalıdır!
            amplitude_asymmetry: ampAsymmetry, 
            coherence:           coherence,    // DÜZƏLİŞ: rawCoherence YOX, coherence olmalıdır!
            phase_lag:           phaseLag
        },
        peak_frequency: {
            hz:      peakFreqHz,   
            z_score: peakFreqZ     
        }
    };

    if (typeof setQeegResult === 'function') setQeegResult(result);
};

export default processEdfFile;